import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'telegram-stars-payment-links',
  title: 'Telegram Stars Payment Links: Create One in 60 Seconds Without Coding',
  metaTitle: 'Telegram Stars Payment Links: Create One in 60 Seconds',
  metaDescription:
    'What a Telegram Stars payment link is, how it works, how to create one without a bot or code, and how to track clicks and conversions with short redirect links.',
  keywords: [
    'telegram stars payment link',
    'telegram stars payment',
    'pay with telegram stars',
    'telegram payment link',
    'telegram invoice link',
    'create telegram stars invoice',
    'telegram stars link generator',
    'telegram stars checkout',
  ],
  category: 'Guides',
  datePublished: '2026-09-16',
  dateModified: '2026-10-15',
  readingTimeMin: 6,
  excerpt:
    'A Telegram Stars payment link opens a native checkout for a fixed amount of Stars. It is the simplest way to sell on Telegram: no bot to build, no server to host. Here is how links work, how to create one and how to get the most out of them.',
  keyTakeaways: [
    'A payment link is a URL generated by the Telegram Bot API (createInvoiceLink) that opens a ready-to-pay Stars invoice in any Telegram client.',
    'You can create links without coding on Telegram Stars Payment Platform: title, description, amount, done.',
    'Short redirect links (telegram-stars.io/r/your-slug) add click tracking and let you change the destination without re-sharing.',
    'Payments arrive in your balance instantly with a ledger entry; withdraw to TON from 100 Stars after the 21-day hold.',
  ],
  body: [
    h2('What is a Telegram Stars payment link?'),
    p(
      'A payment link (also called an invoice link) is a special Telegram URL that, when opened, shows the user a native payment sheet with a product title, description, picture and a price in Stars. The user confirms with one tap, Telegram debits their Stars and the seller is credited. Behind the scenes the link is produced by the Bot API method `createInvoiceLink` with `currency="XTR"`.',
    ),
    p(
      'Unlike an invoice sent as a message to one user, a link is reusable and shareable. The same link can be posted in a channel, placed in a website button, sent in a DM or printed as a QR code, and every click produces an independent payment.',
    ),

    h2('How to create a Telegram Stars payment link without code'),
    ol([
      '[Log in with Telegram](/login) on Telegram Stars Payment Platform. Account creation is free.',
      'Open **Invoices** and click **New invoice**.',
      'Fill in the **title** (what the customer sees as the product name), a **description** of what they get, and the **amount in Stars**.',
      'Click **Create**. The link is generated through the platform\'s bot and shown immediately.',
      'Copy the link, or create a **redirect** with a memorable slug for tracking (see below).',
    ]),
    callout('Need many links? Use the REST API to create invoices programmatically and receive a webhook for each payment. See the [developer guide](/blog/telegram-stars-bot-api-developer-guide).'),

    h2('Short redirect links and click tracking'),
    p(
      'Raw Telegram invoice links are long and opaque. Telegram Stars Payment Platform lets you create a short redirect such as `telegram-stars.io/r/premium-course` that forwards to your payment link. Redirects give you:',
    ),
    ul([
      '**Click counts** per link, so you can compare a channel post against an Instagram bio or an email campaign.',
      '**Editable destinations**: point the slug to a new invoice (new price, new product) without re-sharing the URL.',
      '**Readable URLs** that look trustworthy in a bio or a printed QR code.',
    ]),

    h2('What the buyer sees'),
    p(
      'Tapping the link opens Telegram (or stays in it) and displays the payment sheet with your title, description and the Stars price. If the user has enough Stars, a single confirmation completes the payment. If they do not, Telegram offers to buy a Stars pack on the spot and then completes the purchase. There is no form to fill in, no card number to type, and the whole flow works on iOS, Android, desktop and web.',
    ),

    h2('What the seller sees'),
    ul([
      'A **real-time balance** in the dashboard, split between total, maturing and available.',
      'A **ledger entry** per payment with the amount, date, link used and payer reference.',
      'A **maturity date** for each payment (21 days after receipt), after which it becomes withdrawable.',
      'A **withdrawal form** to pay out to any TON wallet from 100 Stars.',
    ]),

    h2('Use cases for payment links'),
    table(
      ['Use case', 'Example link setup'],
      [
        ['Channel paywall', '"Premium Access – 1 month" · 250 Stars · pinned in the channel'],
        ['Digital download', '"Notion template pack" · 150 Stars · link in Telegram bio and website'],
        ['Consultation', '"30-min call" · 1,000 Stars · sent by DM after a lead qualifies'],
        ['Tips / donations', '"Support the channel" · 50 Stars · posted under each article'],
        ['Event ticket', '"Webinar seat" · 500 Stars · QR code on a flyer'],
      ],
    ),
    p('More ideas in [How to sell digital products on Telegram with Stars](/blog/sell-digital-products-telegram-stars).'),

    h2('Tips for higher conversion'),
    ul([
      'Keep the **title under 32 characters** so it is fully visible in the payment sheet.',
      'Use the description to state **exactly what happens after payment** ("You will be added to the private group within 1 minute").',
      'Match prices to common Stars packs: **50, 100, 250, 500, 1,000**.',
      'Post the link with a **clear call to action** and repeat it in the pinned message.',
      'Create **one link per campaign** and compare click-to-payment ratios.',
    ]),

    h2('Payment links vs sending invoices from a bot'),
    p(
      'If you run your own bot, you can send invoices directly in a chat with `sendInvoice`. That is ideal for conversational flows (the user asks for something, the bot answers with a price). Links are better for broadcasting: one URL, unlimited buyers, no need for the buyer to have interacted with your bot first. Many sellers use both.',
    ),

    h2('Security and trust'),
    p(
      'Payment links never expose card data; payments are settled by Telegram. Buyers can see the name of the bot issuing the invoice, so use a recognisable bot name and avatar. Refunds are possible at any time from the seller side, and Telegram\'s 21-day hold protects buyers from fraudulent sellers. Learn more in [Refunds and payout rules](/blog/telegram-stars-refunds-21-day-hold).',
    ),
  ],
  faq: [
    {
      q: 'Can I create a Telegram Stars payment link without a bot?',
      a: 'Yes. Telegram Stars Payment Platform generates the link for you through its own bot. You only need to sign in with Telegram and fill in a title, description and amount.',
    },
    {
      q: 'Do payment links expire?',
      a: 'Links created on the platform stay valid until you disable them. You can also edit a redirect slug to point to a new invoice at any time.',
    },
    {
      q: 'Can one link be paid by many people?',
      a: 'Yes. A payment link is reusable; each click creates a separate payment credited to your balance.',
    },
    {
      q: 'Can I set a price in dollars instead of Stars?',
      a: 'Stars invoices are priced in Stars. The dashboard shows an approximate conversion to help you choose the amount, but buyers always see Stars.',
    },
    {
      q: 'How do I know which post generated a sale?',
      a: 'Create a separate redirect link per post or campaign. The dashboard shows clicks per redirect and payments per invoice.',
    },
  ],
  relatedSlugs: [
    'telegram-stars-payment',
    'how-to-accept-telegram-stars-payments',
    'sell-digital-products-telegram-stars',
    'telegram-stars-bot-api-developer-guide',
  ],
};

export default article;
